import {
    NAVIGATION_VIEW,
    NavigationPaths
} from "../navigation/NavigationController"
import {
    DateAPI,
    GetPatientsOfProfessionalResponsePayload,
    ValueType
} from "pokal_api"
import { useAppSelector } from "../redux/store/hooks"
import { selectDashboardSelectedPatient } from "../redux/ProfessionalDashboard"
import DashboardWrapper from "../component/DashboardWrapper"
import Card from "../component/Card"
import SmallHeadingComponent from "../component/SmallHeadingComponent"
import AnalyseTriggerCard from "../component/analysis/shared/AnalyseTriggerCard"
import Link from "../component/Link"
import ReportList from "../component/dashboard_report/ReportList"
import NoteList from "../component/NoteList"
import RequirePatientSelection from "../component/require/RequirePatientSelection"
import ValueTypeOverviewComponent from "../component/ValueTypeOverviewComponent"
import { useEffect, useState } from "react"
import { ArrayElement } from "../types/ArrayElement"
import mockPatient from "../onboarding/mock/mockPatient"
import {
    hidePatientOverviewMedication,
    mockPatientUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../redux/Onboarding"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "../redux/store/hooks"
import { showPersistentNotification } from "../redux/Notification"

const DATA_CARD_TYPES: { focused: ValueType; secondary?: ValueType }[] = [
    { focused: ValueType.PHQUESTIONNAIRE },
    { focused: ValueType.SFQUESTIONNAIRE },
    { focused: ValueType.MOOD },
    { focused: ValueType.STEPS },
    { focused: ValueType.SLEEP_DURATION, secondary: ValueType.SLEEP_LATENCY },
    { focused: ValueType.SLEEP_HEART_RATE, secondary: ValueType.SLEEP_HEART_RATE_VARIABILITY },
]

function PatientOverview() {
    const [patient, setPatient] =
        useState<ArrayElement<GetPatientsOfProfessionalResponsePayload> | null>(
            null
        )

    const fetchedPatient = useAppSelector(selectDashboardSelectedPatient)
    const mockedPatient = mockPatient()
    const dispatch = useAppDispatch()

    const running = useAppSelector(selectOnboardingIsRunningState)
    const step = useAppSelector(selectOnboardingStep)

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            setPatient(mockedPatient[0])
        } else {
            setPatient(fetchedPatient)
        }
    }, [running, step, fetchedPatient])

    useEffect(() => {
        if (patient && patient.bmi === 100) {
            showPersistentNotification(
                dispatch,
                "Standard-BMI erkannt",
                `Der BMI von ${patient.firstName} ${patient.lastName} beträgt 100, was der von der Studienleitung gesetzte Standardwert ist. Bitte bitten Sie den Patienten/die Patientin, in der App unter Einstellungen sein/ihr Gewicht und seine/ihre Größe einzutragen und zu speichern.`
            )
        }
    }, [patient, dispatch])

    const navigate = useNavigate()

    function genderLabel(gender: string | null | undefined): string {
        if (gender === "male") return "Männlich"
        if (gender === "female") return "Weiblich"
        return "Sonstiges"
    }

    return (
        <RequirePatientSelection>
            {patient !== null && (
                <DashboardWrapper
                    onClick={() => {
                        navigate(NavigationPaths[NAVIGATION_VIEW.PATIENT_SELECTION_VIEW].path)
                    }}
                    title={`${patient.firstName} ${patient.lastName}`}>
                    <div className={"flex space-y-4 flex-col"}>
                        <div className={"flex flex-col space-y-2"}>
                            <div className={"flex"}>
                                <div className={"w-40 font-bold"}>Geburtstag</div>
                                <div>
                                    {DateAPI.timestampToString(
                                        new Date(patient.birthdate).getTime(),
                                        false
                                    )}
                                </div>
                            </div>
                            <div className={"flex"}>
                                <div className={"w-40 font-bold"}>BMI</div>
                                <div>{patient.bmi}</div>
                            </div>
                            <div className={"flex"}>
                                <div className={"w-40 font-bold"}>Geschlecht</div>
                                <div>{genderLabel(patient.gender)}</div>
                            </div>
                        </div>
                        <div>
                            Diese Seite gibt einen groben Überblick über
                            wichtige Gesundheitsindikatoren des ausgewählten
                            Patienten oder der ausgewählten Patientin. Die Werte
                            beziehen sich, sofern nicht anders angegeben, auf
                            14-Tages-Durchschnitte. Sie können auf die einzelnen
                            Karten klicken, um weitere Einzelheiten zu den
                            jeweiligen Datenpunkten zu erfahren.
                        </div>
                        <ReportList />
                        <NoteList />

                        <AnalyseTriggerCard
                            title="Analyse"
                            description="KI-gestützte Analyse der Patientendaten und Leitlinien-Assistent."
                            onOpen={() => navigate(NavigationPaths[NAVIGATION_VIEW.PATIENT_ANALYSE_VIEW].path)}
                        />

                        <div className="grid grid-cols-2 gap-4">
                            {DATA_CARD_TYPES.map(({ focused, secondary }) => (
                                <ValueTypeOverviewComponent key={focused} focused={focused} secondary={secondary} />
                            ))}
                        </div>

                        <Card
                            blur={hidePatientOverviewMedication(running, step)}
                            width={"100%"}>
                            <div className={"flex flex-col space-y-4"}>
                                <SmallHeadingComponent>
                                    Medikation
                                </SmallHeadingComponent>
                                <div className={"flex space-x-1"}>
                                    <div>
                                        Für Details zu Medikation der Patient:in
                                        klicken Sie bitte
                                    </div>
                                    <Link
                                        to={
                                            NavigationPaths[
                                                NAVIGATION_VIEW
                                                    .PROFESSIONAL_DASHBOARD_MEDICATION
                                            ].path
                                        }>
                                        hier.
                                    </Link>
                                </div>
                            </div>
                        </Card>
                    </div>
                </DashboardWrapper>
            )}
        </RequirePatientSelection>
    )
}

export default PatientOverview
